import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import classNames from 'classnames';
import {
    Dropdown,
    DropdownItemProps,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import { AutoSuggest, EnumMap } from '@instinet-analytics/core-lib';
import {
    FilterValueType,
    MembershipSign,
    signDoesNotNeedValue,
    signTextMap,
} from 'utils/filter/filter';
import PillInput from 'utils/components/PillInput/PillInput';
import { isComparisonValueValid } from 'utils/components/FiltersModal/comparison/FilterComparison';
import 'utils/components/FiltersModal/comparison/ComparisonControl.scss';
import './MembershipComparisonControl.scss';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import PillDropdown from 'utils/components/PillInput/PillDropdown';

interface MembershipComparisonControlProps<TDataItem> {
    columnName?: string | null;
    fieldName?: string | null;
    data?: TDataItem[];
    staticData?: { [dimension: string]: string[] };
    valueType: FilterValueType;
    enumMap?: EnumMap;
    sign: MembershipSign | undefined;
    values: string[];
    onSignChange: (sign: MembershipSign) => void;
    onValuesChange: (values: string[], commitOnSet: boolean) => void;
    onInputEnter?: () => void;
    error?: boolean;
    errorMessage?: string;
}

const CandidatesDropdownThreshold = 10;

export default function MembershipComparisonControl<TDataItem>(
    props: MembershipComparisonControlProps<TDataItem>
) {
    const {
        columnName,
        fieldName,
        data,
        staticData,
        valueType,
        sign,
        values,
        onSignChange,
        onValuesChange,
        onInputEnter,
        error,
        errorMessage,
        enumMap,
    } = props;

    const [candidates, setCandidates] = useState<string[]>([]);

    const allowedSigns =
        valueType === ValueType.ENUM
            ? [MembershipSign.INCLUDE, MembershipSign.EXCLUDE]
            : [
                  MembershipSign.INCLUDE,
                  MembershipSign.EXCLUDE,
                  MembershipSign.CONTAINS,
                  MembershipSign.NOTCONTAINS,
                  MembershipSign.STARTSWITH,
                  MembershipSign.ENDSWITH,
                  MembershipSign.BLANK,
                  MembershipSign.NOTBLANK,
              ];

    const signOptions: DropdownItemProps[] = allowedSigns.map((x) => ({
        key: x,
        value: x,
        text: signTextMap[x],
        className: 'comparison-' + x,
    }));

    const isValid = values.every((x) => isComparisonValueValid(valueType, x));

    const finalError = isValid ? error : true;
    const finalErrorMessage = isValid ? errorMessage : '';

    const signSymbol = sign ? signTextMap[sign] : '';
    const signClass =
        signSymbol.length > 7
            ? 'width-xl'
            : signSymbol.length > 1
            ? 'width-l'
            : '';

    useEffect(() => {
        if (!fieldName) {
            return;
        }
        if (staticData && staticData[fieldName]) {
            setCandidates(staticData[fieldName]);
            return;
        }
        const allCandidates = data
            ? data
                  .filter((i) => !isNil(i))
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((i) => (i as any)[fieldName])
                  .filter((i) => !isNil(i))
            : [];
        const uniqCandidates = uniq(allCandidates).sort();
        setCandidates(uniqCandidates);
    }, [data, fieldName, staticData]);

    const renderInput = CandidatesAutoSuggest;

    const signSupportsDropdown =
        sign === MembershipSign.INCLUDE || sign === MembershipSign.EXCLUDE;
    const showDropdown =
        signSupportsDropdown &&
        candidates.length > 0 &&
        candidates.length <= CandidatesDropdownThreshold;

    const onPillsChanged = (pills: string[]) => {
        onValuesChange(pills, showDropdown);
    };

    return (
        <div
            className={classnames(
                'ComparisonControl',
                'MembershipComparisonControl',
                signClass,
                finalError && 'error'
            )}
        >
            <div className="input-with-select large">
                {finalErrorMessage && (
                    <div className="error-message">{finalErrorMessage}</div>
                )}
                <Dropdown
                    className="sign"
                    options={signOptions}
                    value={sign}
                    onChange={onSignChange}
                />

                {!signDoesNotNeedValue(sign) && !showDropdown && (
                    <PillInput
                        pills={values}
                        onPillsChanged={onPillsChanged}
                        onEnter={onInputEnter}
                        placeholder={`Enter a ${columnName}`}
                        renderInput={renderInput}
                        confirmOnChange={showDropdown}
                    />
                )}
                {!signDoesNotNeedValue(sign) && showDropdown && (
                    <PillDropdown
                        className={'CandidatesDropdown'}
                        enumMap={enumMap}
                        pills={values}
                        options={candidates}
                        onPillsChanged={onPillsChanged}
                        placeholder={`Select a ${columnName}`}
                    />
                )}
            </div>
        </div>
    );

    type CandidatesAutoSuggestProps = React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    >;

    function CandidatesAutoSuggest(props: CandidatesAutoSuggestProps) {
        const { value, onKeyDown, onBlur, onChange } = props;

        const onGetSuggestion = useCallback((c: string) => c, []);
        const onGetSuggestionValue = useCallback((c: string) => c, []);

        const onChanged = (nextValue: string) => {
            if (onChange) {
                const ev = {
                    target: { value: nextValue },
                } as ChangeEvent<HTMLInputElement>;
                onChange(ev);
            }
        };

        const className = classNames(
            'AutoSuggestSymbol',
            showDropdown && 'hidden'
        );

        return (
            <AutoSuggest
                autoFocus={true}
                className={className}
                candidates={
                    (value ?? '').toString().length > 0 ? candidates : []
                }
                value={(value as string) ?? ''}
                getComparisonString={onGetSuggestion}
                getCandidateValue={onGetSuggestionValue}
                placeholder={`Enter a ${columnName}`}
                onChange={onChanged}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        );
    }
}
